import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  getPreferences, recordFunderFeedback,
  getMatches, getFunderMatches, getDigestHistory, getCoFunders,
} from '../api'
import { getClientId, getOrgName } from '../session'
import Badge from '../components/Badge'
import Button from '../components/Button'
import GrantCard from '../components/GrantCard'
import FunderCard from '../components/FunderCard'
import CoFunders from '../components/CoFunders'
import UpgradePlans from '../components/UpgradePlans'
import { currentPaidTier } from '../plans'
import { ErrorAlert } from '../components/Alert'
import { useDocumentTitle } from '../components/PublicLayout'

// How many matches to show before the "Show all" expander.
const INITIAL_VISIBLE = 8
// Foundation leads shown before the expander (the API caps the list at 25).
const FUNDERS_INITIAL_VISIBLE = 5

/**
 * Foundation lead card (Phase 9C). Same compact expandable pattern as
 * GrantCard, but foundations are LEADS, not opportunities — no deadline or
 * urgency badge; instead the Part XV application info, restrictions, and the
 * IRS-filing freshness label.
 */
/**
 * What a past digest actually contained.
 *
 * "0 grants included" was technically true and practically a lie: a digest
 * carried by foundation leads alone is the NORMAL send for direct-service
 * orgs, and reading "0" makes a successful digest look like a failure.
 *
 * funders_count is null (not 0) for digests sent before we recorded leads
 * (migration 0018) — "we don't know" is not "there were none", so those rows
 * keep the old grants-only wording rather than claim zero foundations.
 */
function digestContents({ grants_count: grants, funders_count: funders }) {
  const parts = []
  if (grants > 0) parts.push(`${grants} grant${grants === 1 ? '' : 's'}`)
  if (funders > 0) parts.push(`${funders} foundation lead${funders === 1 ? '' : 's'}`)
  if (parts.length) return `${parts.join(' · ')} included`
  if (funders === null || funders === undefined) return `${grants} grants included`
  return 'No matches that week'
}


/**
 * Layout-shaped loading skeleton (8D). Mirrors the dashboard structure —
 * header, stat cards, match rows — so the page settles into place instead of
 * flashing from a centered spinner to a full layout.
 */
function DashboardSkeleton() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-10 space-y-8 animate-pulse" aria-busy="true">
      <div>
        <div className="h-7 w-56 bg-gray-200 rounded" />
        <div className="h-4 w-72 bg-gray-200 rounded mt-2" />
      </div>
      <div className="grid grid-cols-3 gap-4">
        {[0, 1, 2].map(i => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="h-7 w-12 bg-gray-200 rounded mx-auto" />
            <div className="h-3 w-16 bg-gray-100 rounded mx-auto mt-2" />
          </div>
        ))}
      </div>
      <div className="h-16 bg-gray-100 rounded-xl border border-gray-200" />
      <div>
        <div className="h-5 w-44 bg-gray-200 rounded mb-4" />
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 flex items-center gap-3 px-4 py-3">
              <div className="h-8 w-10 bg-gray-200 rounded flex-shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="h-4 w-3/4 bg-gray-200 rounded" />
                <div className="h-3 w-1/2 bg-gray-100 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
      <span className="sr-only">Loading your dashboard…</span>
    </div>
  )
}

/** Slim, dismissible-feeling upgrade banner that expands into the full plan grid. */
function UpgradeBanner({ prefs }) {
  const [open, setOpen] = useState(false)
  const isTrial = prefs?.plan_status === 'trial'

  return (
    <div className="rounded-xl border border-brand-200 bg-brand-50/60">
      <div className="flex items-center justify-between gap-4 px-5 py-4">
        <div>
          <p className="font-semibold text-gray-900 text-sm">
            {isTrial ? 'Choose a plan to keep your digests' : 'Upgrade your plan'}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {isTrial
              ? 'Pick a plan before your trial ends. Cancel anytime.'
              : 'Unlock more sources, focus areas, and features.'}
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => setOpen(o => !o)}>
          {open ? 'Hide plans' : 'See plans'}
        </Button>
      </div>
      {open && (
        <div className="px-5 pb-5 pt-1">
          <UpgradePlans currentTier={currentPaidTier(prefs)} />
        </div>
      )}
    </div>
  )
}

export default function Dashboard() {
  useDocumentTitle('Dashboard')
  const navigate = useNavigate()
  const clientId = getClientId()
  const orgName  = getOrgName() || 'Your Organization'

  const [prefs, setPrefs]           = useState(null)
  const [grants, setGrants]         = useState([])
  const [funderData, setFunderData] = useState(null)
  const [coFunders, setCoFunders]   = useState(null)
  const [digestHistory, setHistory] = useState([])
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState('')
  const [planEnded, setPlanEnded]   = useState(false)
  const [showAll, setShowAll]       = useState(false)
  const [showAllFunders, setShowAllFunders] = useState(false)

  useEffect(() => {
    if (!clientId) { navigate('/signin'); return }
    Promise.all([
      getPreferences(),
      getMatches(),
      // Foundations must never break the rest of the dashboard — degrade to
      // hiding the section if this call fails.
      getFunderMatches().catch(() => null),
      getDigestHistory(),
      // Same rule: a failure here hides the section rather than showing an
      // error where an answer about the client's funders belongs. An error
      // banner in that slot reads as "none", which is the one thing this
      // feature must never say.
      getCoFunders().catch(() => null),
    ])
      .then(([p, g, f, h, c]) => {
        setPrefs(p); setGrants(g); setFunderData(f); setHistory(h); setCoFunders(c)
      })
      .catch(err => {
        // The API clears the cached session on a 401; bounce to sign-in.
        if (!getClientId()) { navigate('/signin'); return }
        // 402: signed in, but the plan ended at the last renewal date. Show the
        // way back rather than a raw error — Account and billing still work.
        if (err.planEnded) { setPlanEnded(true); return }
        setError(err.message)
      })
      .finally(() => setLoading(false))
  }, [clientId])

  if (loading) return <DashboardSkeleton />

  // The plan ended at the last renewal date. The session is still valid and
  // Account + billing still work, so this is a dead end only for the product.
  if (planEnded) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-brand-900">Your subscription has ended</h1>
        <p className="mt-3 text-gray-600 leading-relaxed">
          Your plan ran through the end of the period you paid for, and has now
          finished. Your organization profile and match history are still here.
          Resubscribe and your Tuesday digest picks up where it left off.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={() => navigate('/account')}>Resubscribe</Button>
          <Button variant="secondary" onClick={() => navigate('/plans')}>
            See plans
          </Button>
        </div>
      </div>
    )
  }

  // Beta accounts are on the house — never nudge them to upgrade/checkout.
  const showUpgrade =
    prefs?.plan_status !== 'beta' &&
    !(prefs?.plan_status === 'active' && prefs?.plan_tier === 'consultant')
  const visibleGrants = showAll ? grants : grants.slice(0, INITIAL_VISIBLE)
  const hiddenCount = grants.length - visibleGrants.length

  const funders = funderData?.funders || []
  const visibleFunders = showAllFunders ? funders : funders.slice(0, FUNDERS_INITIAL_VISIBLE)

  return (
    <div className="max-w-3xl mx-auto px-4 py-10 space-y-8">
      <ErrorAlert message={error} />

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{orgName}</h1>
        <p className="text-gray-500 text-sm mt-1">
          Plan: <span className="font-medium capitalize">{prefs?.plan_tier || 'Not set'}</span>
          {' · '}
          Status: <span className={`font-medium capitalize ${
            prefs?.plan_status === 'active' ? 'text-green-600' :
            prefs?.plan_status === 'beta'   ? 'text-brand-600' :
            prefs?.plan_status === 'trial'  ? 'text-yellow-600' : 'text-red-500'
          }`}>{prefs?.plan_status || 'Not set'}</span>
        </p>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Focus Areas', value: prefs?.focus_areas?.length ?? 'Not set' },
          { label: 'State', value: prefs?.state || 'Not set' },
          // "(Tuesdays)" lives in the label — a long value overflows the card on mobile.
          {
            label: prefs?.digest_frequency === 'weekly' ? 'Digest (Tuesdays)' : 'Digest',
            value: prefs?.digest_frequency || 'Not set',
          },
        ].map(({ label, value }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-lg sm:text-2xl font-bold text-gray-900 capitalize truncate">{value}</p>
            <p className="text-xs text-gray-500 mt-1">{label}</p>
          </div>
        ))}
      </div>

      {/* Upgrade banner — surfaced near the top so it isn't buried under matches. */}
      {showUpgrade && <UpgradeBanner prefs={prefs} />}

      {/* Grant matches section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Recent Grant Matches
            {grants.length > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500">{grants.length}</span>
            )}
          </h2>
          <Link to="/preferences">
            <Button variant="secondary" size="sm">Edit Preferences</Button>
          </Link>
        </div>

        {grants.length > 0 ? (
          <>
            <p className="text-xs text-gray-500 mb-3">Click a match to see why it fits and give feedback.</p>
            <div className="space-y-2">
              {visibleGrants.map(grant => (
                <GrantCard
                  key={grant.grant_id}
                  grant={grant}
                  clientId={clientId}
                  onFeedback={(gid, val) =>
                    setGrants(gs => gs.map(g => g.grant_id === gid ? { ...g, feedback: val } : g))
                  }
                />
              ))}
            </div>
            {grants.length > INITIAL_VISIBLE && (
              <div className="mt-4 text-center">
                <Button variant="ghost" size="sm" onClick={() => setShowAll(s => !s)}>
                  {showAll ? 'Show fewer' : `Show all ${grants.length} matches`}
                  {!showAll && hiddenCount > 0 && ` (+${hiddenCount})`}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center">
            {/* Zero federal matches is CORRECT output for most direct-service
                orgs (ADR-0008), not a pending state. If foundation leads exist,
                matching has demonstrably run — saying "your first digest is on
                its way" would be false, and it frames the right answer as a
                failure. Mirrors the wording the digest email already uses. */}
            {funders.length > 0 ? (
              <>
                <div className="text-4xl mb-3">🔍</div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  No federal programs match your mission right now
                </h3>
                <p className="text-sm text-gray-500 max-w-md mx-auto">
                  That is normal for direct-service work, since most federal grants go
                  to research institutions and government agencies. The
                  foundations below are where your opportunities usually are, and
                  we keep checking federal sources every 6 hours.
                </p>
              </>
            ) : (
              <>
                <div className="text-4xl mb-3">📬</div>
                <h3 className="font-semibold text-gray-900 mb-1">Your first digest is on its way</h3>
                <p className="text-sm text-gray-500 max-w-sm mx-auto">
                  Praenum checks for new grants every 6 hours. Your personalized matches
                  will appear here and be emailed to you every Tuesday morning.
                </p>
              </>
            )}
            <p className="text-xs text-gray-500 mt-4">
              Monitoring: <span className="font-medium">{prefs?.focus_areas?.join(', ') || 'Not set'}</span>
              {prefs?.state && <> in <span className="font-medium">{prefs.state}</span></>}
            </p>
          </div>
        )}
      </div>

      {/* Foundations to approach (Phase 9C). Every paid tier gets cards
          (owner decision 2026-07-16), so this is a standing section: it shows
          the ranked leads, or an empty state until the monthly refresh finds
          one. */}
      {funderData && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-semibold text-gray-900">
              Foundations to Approach
              {funders.length > 0 && (
                <span className="ml-2 text-sm font-normal text-gray-500">{funders.length}</span>
              )}
            </h2>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Private foundations that fund organizations like yours and accept
            applications: leads from their IRS 990-PF filings, not open calls.
          </p>

          {funders.length > 0 ? (
            <>
              <div className="space-y-2">
                {visibleFunders.map(funder => (
                  <FunderCard
                    key={funder.funder_id}
                    funder={funder}
                    clientId={clientId}
                    onFeedback={(fid, val) =>
                      setFunderData(d => ({
                        ...d,
                        funders: d.funders.map(f => f.funder_id === fid ? { ...f, feedback: val } : f),
                      }))
                    }
                  />
                ))}
              </div>
              {funders.length > FUNDERS_INITIAL_VISIBLE && (
                <div className="mt-4 text-center">
                  <Button variant="ghost" size="sm" onClick={() => setShowAllFunders(s => !s)}>
                    {showAllFunders ? 'Show fewer' : `Show all ${funders.length} foundations`}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center">
              <div className="text-4xl mb-3">🏛️</div>
              <h3 className="font-semibold text-gray-900 mb-1">No foundation leads yet</h3>
              <p className="text-sm text-gray-500 max-w-sm mx-auto">
                Foundation matches refresh monthly as new IRS 990-PF filings are
                published. When a foundation that funds organizations like yours
                shows up, it will appear here and in your digest.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Foundations that fund alongside yours (#61, ADR-0014). Placed AFTER
          "Foundations to Approach" on purpose: that section is the ranked
          product, this one is derived from the client's own 990 record and is
          a different kind of evidence. Its own heading and lead-in keep the two
          from reading as one list. */}
      {/* orgName/state come from the PROFILE, not the cached session summary:
          they are what the #65 resolver looks the organization up by, and
          Preferences is where a client corrects them. `state` is nullable, and
          OrgIdentitySetup degrades to the typed EIN field when it is missing. */}
      <CoFunders
        data={coFunders}
        orgName={prefs?.org_name}
        state={prefs?.state}
        orgEin={prefs?.org_ein}
        // Refreshes BOTH: writing org_ein changes the co-funder answer and the
        // profile it was read from, and a stale `orgEin` would keep telling the
        // client their old EIN "appears in no 990" after they replaced it.
        onRefresh={() =>
          Promise.all([
            getCoFunders().then(setCoFunders),
            getPreferences().then(setPrefs),
          ]).catch(() => {})
        }
      />

      {/* Digest history section */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Digest History</h2>
        {digestHistory.length > 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {digestHistory.map(digest => (
              <div key={digest.id} className="flex items-center justify-between px-5 py-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">{digest.sent_at}</p>
                  <p className="text-xs text-gray-500">{digestContents(digest)}</p>
                </div>
                <Badge variant="open">Sent</Badge>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center">
            <div className="text-4xl mb-3">🗞️</div>
            <h3 className="font-semibold text-gray-900 mb-1">No digests yet</h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto">
              Your first digest lands Tuesday morning. Every send will be listed
              here so you can see exactly what went out and when.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
